/**
 * The edits on a customer's Settings page, held by the page (not by each card)
 * so its one save bar can see, summarise, send and reset all of them. One
 * draft per owning module: `usePaymentDraft` holds credit control's payment
 * methods and, for credit account, terms, PO rule, PO format and limit, with the
 * inline validation the Payment methods card shows; `useInvoicingDraft` holds
 * the sales ledger's invoicing settings, and knows which of them changed (the
 * save sends only those).
 *
 * A draft starts from, and returns to, the saved values it is given. A newer
 * saved value (after a save) replaces the draft; a change that leaves the saved
 * values alone, such as a stop being put on or lifted, keeps the edits.
 */
import { useState } from "react";
import type {
  CustomerCreditView,
  PaymentMethod,
  PaymentSettingsValues,
  PaymentTermsValues,
  PoRule,
  TermsBasis,
} from "@shared-core/data/credit-control-types";
import type {
  InvoicingSettingsValues,
  InvoicingSettingsView,
} from "@shared-core/data/sales-ledger-types";
import {
  creditLimitPenceFromInput,
  creditLimitPounds,
  isInvalidCreditLimitInput,
} from "@shared-core/domain/credit-control-presentation";

/** The payment values as saved: the account's, or card only for a customer with no billing account. */
export function savedPaymentValues(
  view: CustomerCreditView,
): PaymentSettingsValues {
  const account = view.billingAccount;
  const hasCredit = view.paymentMethods.includes("credit_account");
  return {
    paymentMethods: view.paymentMethods,
    terms:
      account && hasCredit && account.terms
        ? {
            termsDays: account.terms.days,
            termsBasis: account.terms.basis,
            poRule: account.poRule,
            poFormat: account.poFormat,
            creditLimitPence: account.creditLimitPence,
          }
        : null,
  };
}

const sameMethods = (a: PaymentMethod[], b: PaymentMethod[]): boolean =>
  a.length === b.length && a.every((m) => b.includes(m));

const limitInputText = (pence: number | null): string =>
  creditLimitPounds(pence)?.toString() ?? "";

export function usePaymentDraft(view: CustomerCreditView) {
  const saved = savedPaymentValues(view);
  // Ticking credit account prefills the policy's terms; no PO is needed until finance says. The limit is never
  // prefilled: no limit, no credit account (Ben, d75), so finance enters one.
  const standard: PaymentTermsValues = {
    termsDays: view.standardTerms.days,
    termsBasis: view.standardTerms.basis,
    poRule: "not_required",
    poFormat: null,
    creditLimitPence: null,
  };
  const base = saved.terms ?? standard;
  const savedTermsDays = String(base.termsDays);
  const savedLimitText = limitInputText(base.creditLimitPence);

  const [methods, setMethods] = useState<PaymentMethod[]>(saved.paymentMethods);
  const [termsDays, setTermsDays] = useState(savedTermsDays);
  const [termsBasis, setTermsBasis] = useState<TermsBasis>(base.termsBasis);
  const [poRule, setPoRule] = useState<PoRule>(base.poRule);
  // The PO format as typed: empty is none. Kept when the rule is switched to "Not required", where it is ignored.
  const [poFormatText, setPoFormatText] = useState(base.poFormat ?? "");
  const [limitText, setLimitText] = useState(savedLimitText);

  const reset = () => {
    setMethods(saved.paymentMethods);
    setTermsDays(savedTermsDays);
    setTermsBasis(base.termsBasis);
    setPoRule(base.poRule);
    setPoFormatText(base.poFormat ?? "");
    setLimitText(savedLimitText);
  };

  // New saved values (a save) replace the draft. A stop put on or lifted does
  // not change them, so edits in progress survive it.
  const savedKey = JSON.stringify(saved);
  const [seenKey, setSeenKey] = useState(savedKey);
  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    reset();
  }

  const credit = methods.includes("credit_account");
  const savedCredit = saved.paymentMethods.includes("credit_account");
  const noMethod = methods.length === 0;
  const days = Number(termsDays);
  const daysInvalid =
    credit &&
    (termsDays.trim() === "" ||
      !Number.isInteger(days) ||
      days < 0 ||
      days > 365);
  const limitInvalid = credit && isInvalidCreditLimitInput(limitText);
  // A credit account always has a limit above zero, entered by finance (never prefilled).
  const limitMissing = credit && !limitInvalid && limitText.trim() === "";

  const values: PaymentSettingsValues = {
    paymentMethods: methods,
    terms: credit
      ? {
          termsDays: daysInvalid ? standard.termsDays : days,
          termsBasis,
          poRule,
          // As typed: the backend trims it, and refuses one that is only spaces.
          poFormat: poFormatText === "" ? null : poFormatText,
          creditLimitPence: creditLimitPenceFromInput(limitText),
        }
      : null,
  };
  const savedTerms = saved.terms;
  const invalid = noMethod || daysInvalid || limitInvalid || limitMissing;
  // An entry refused inline still differs from what is saved, even where the
  // fallback value it is sent as would equal it.
  const dirty =
    invalid ||
    !sameMethods(methods, saved.paymentMethods) ||
    (values.terms !== null &&
      (savedTerms === null ||
        values.terms.termsDays !== savedTerms.termsDays ||
        values.terms.termsBasis !== savedTerms.termsBasis ||
        values.terms.poRule !== savedTerms.poRule ||
        poFormatText.trim() !== (savedTerms.poFormat ?? "") ||
        values.terms.creditLimitPence !== savedTerms.creditLimitPence));

  const toggle = (method: PaymentMethod, checked: boolean) => {
    setMethods((current) =>
      checked ? [...current, method] : current.filter((m) => m !== method),
    );
    if (method === "credit_account" && checked) {
      setTermsDays(String(standard.termsDays));
      setTermsBasis(standard.termsBasis);
      setPoRule(standard.poRule);
      setPoFormatText("");
      setLimitText(limitInputText(standard.creditLimitPence));
    }
  };

  return {
    methods,
    termsDays,
    setTermsDays,
    termsBasis,
    setTermsBasis,
    poRule,
    setPoRule,
    poFormatText,
    setPoFormatText,
    limitText,
    setLimitText,
    toggle,
    reset,
    saved,
    values,
    credit,
    savedCredit,
    noMethod,
    daysInvalid,
    limitInvalid,
    limitMissing,
    /** Something typed is refused inline; Save waits until it is fixed. */
    invalid,
    dirty,
  };
}

export type PaymentDraft = ReturnType<typeof usePaymentDraft>;

/** The invoicing settings as saved, or null while the sales ledger is off (the page then shows none). */
export function savedInvoicingValues(
  invoicing: InvoicingSettingsView | null,
): InvoicingSettingsValues | null {
  if (!invoicing) return null;
  return {
    madeToOrderInvoiceTrigger: invoicing.madeToOrderInvoiceTrigger.value,
    postageInvoicing: invoicing.postageInvoicing.value,
    stockBilling: invoicing.stockBilling.value,
    postsOnOwnCarrierAccount: invoicing.postsOnOwnCarrierAccount.value,
    invoiceGrouping: invoicing.invoiceGrouping.value,
    invoiceCutOff: invoicing.invoiceCutOff.value,
    invoiceCutOffDay: invoicing.invoiceCutOffDay.value,
  };
}

const INVOICING_KEYS = [
  "madeToOrderInvoiceTrigger",
  "postageInvoicing",
  "stockBilling",
  "postsOnOwnCarrierAccount",
  "invoiceGrouping",
  "invoiceCutOff",
  "invoiceCutOffDay",
] as const satisfies readonly (keyof InvoicingSettingsValues)[];

export function useInvoicingDraft(view: CustomerCreditView) {
  const saved = savedInvoicingValues(view.invoicing);
  const [values, setValues] = useState(saved);

  const reset = () => setValues(saved);

  // As the payment draft: newer saved values (a save) replace the draft.
  const savedKey = JSON.stringify(saved);
  const [seenKey, setSeenKey] = useState(savedKey);
  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    reset();
  }

  const set = <K extends keyof InvoicingSettingsValues>(
    key: K,
    value: InvoicingSettingsValues[K],
  ) =>
    setValues((current) => {
      if (!current) return current;
      // A day belongs to the cut-off it was named for. Choosing a cut-off fills its day: the one saved for it, else
      // the backend's default for it (Friday weekly, the last day monthly); When due has none, so it clears.
      if (key !== "invoiceCutOff") return { ...current, [key]: value };
      const cutOff = value as string;
      const day =
        saved && cutOff === saved.invoiceCutOff
          ? saved.invoiceCutOffDay
          : (view.invoicing?.cutOffDayDefaults[cutOff] ?? "");
      return {
        ...current,
        invoiceCutOff: value as never,
        invoiceCutOffDay: day,
      };
    });

  // Only the settings that differ from what is saved: what the save sends (#2375's shape). No day ("", under
  // When due) is not a change: the backend reads no day under When due.
  const changes: Partial<InvoicingSettingsValues> =
    values && saved
      ? Object.fromEntries(
          INVOICING_KEYS.filter(
            (key) =>
              values[key] !== saved[key] &&
              !(key === "invoiceCutOffDay" && values[key] === ""),
          ).map((key) => [key, values[key]]),
        )
      : {};

  return {
    /** The read's settings: wording, options, last changes, where to save. Null while the sales ledger is off. */
    invoicing: view.invoicing,
    saved,
    values,
    set,
    reset,
    changes,
    dirty: Object.keys(changes).length > 0,
  };
}

export type InvoicingDraft = ReturnType<typeof useInvoicingDraft>;
